import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Alert } from '@/design-system/alert';
import { Button } from '@/design-system/button';
import { TableData, type TableDataColumn } from '@/design-system/table-data';

import { warehouseApi } from '../../api/warehouse.api';
import { GoodsReceiptTable } from '../../components/GoodsReceiptTable';
import {
  HistorySection,
  purchaseOrderHistory,
} from '../../components/history';
import {
  PoSourceKVs,
  PoTypeTag,
  TransferCancelHint,
} from '../../components/orderType';
import {
  codeCls,
  DetailHeader,
  DetailSkeleton,
  KV,
  LoadError,
  MappedBadge,
  metaCls,
  nameCls,
  numCls,
  ProgressCell,
  Section,
} from '../../components/ui';
import PurchaseOrderFormModal from '../../components/PurchaseOrderFormModal';
import { usePurchaseOrderActions } from '../../components/usePurchaseOrderActions';
import { fmtDateTime, fmtQty } from '../../format';
import { useLoad } from '../../hooks/useLoad';
import WarehousePrintModal, {
  type PrintTarget,
} from '../../print/WarehousePrintModal';
import { PO_STATUS, RECEIPT_STATUS } from '../../status';
import type { PurchaseOrderLine } from '../../types';
import { useWarehouse } from '../../WarehouseContext';
import { WAREHOUSE_PATHS } from '../../paths';

export const LINE_COLUMNS: TableDataColumn<PurchaseOrderLine>[] = [
  {
    key: 'sku',
    title: 'SKU',
    render: (_v, l) => <span className={codeCls}>{l.sku}</span>,
  },
  {
    key: 'itemName',
    title: 'Product name',
    render: (_v, l) => <span className={nameCls}>{l.itemName}</span>,
  },
  {
    key: 'uomName',
    title: 'UoM',
    render: (_v, l) => <span className={metaCls}>{l.uomName}</span>,
  },
  {
    key: 'qty',
    title: 'Qty',
    align: 'right',
    render: (_v, l) => <span className={numCls}>{fmtQty(l.qty)}</span>,
  },
  {
    key: 'qtyReceived',
    title: 'Received',
    align: 'right',
    render: (_v, l) => <span className={numCls}>{fmtQty(l.qtyReceived)}</span>,
  },
  {
    key: 'qtyRemaining',
    title: 'Remaining',
    align: 'right',
    render: (_v, l) => (
      <span
        className={`font-mono text-[12px] font-semibold tabular-nums ${
          l.qtyRemaining > 0 ? 'text-text-strong-950' : 'text-text-soft-400'
        }`}
      >
        {fmtQty(Math.max(0, l.qtyRemaining))}
      </span>
    ),
  },
];


const PurchaseOrderDetailPage: React.FC = () => {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { refreshToken } = useWarehouse();
  const detail = useLoad(
    (signal) => warehouseApi.getPurchaseOrder(id, { signal }),
    [id, refreshToken]
  );
  const actions = usePurchaseOrderActions(detail.reload);
  const [print, setPrint] = useState<PrintTarget | null>(null);
  const [editing, setEditing] = useState(false);

  if (detail.loading && !detail.data) return <DetailSkeleton />;
  if (detail.error || !detail.data) {
    return (
      <div className="space-y-5 p-6">
        <DetailHeader
          backTo={WAREHOUSE_PATHS.purchaseOrder}
          backLabel="Purchase order"
          title={id}
        />
        <LoadError
          message={detail.error?.message ?? 'Purchase order tidak ditemukan'}
          onRetry={detail.reload}
        />
      </div>
    );
  }
  const { order: x, lines, goodsReceipts } = detail.data;
  const open = x.status === 'CONFIRMED' && x.receiptStatus !== 'FINISHED';

  return (
    <div className="space-y-5 p-6">
      <DetailHeader
        backTo={WAREHOUSE_PATHS.purchaseOrder}
        backLabel="Purchase order"
        meta={
          <>
            Dibuat {fmtDateTime(x.createdAt)} · oleh{' '}
            <span className="font-semibold text-text-sub-600">
              {x.createdBy}
            </span>
            {x.confirmedAt && ` · confirmed ${fmtDateTime(x.confirmedAt)}`}
          </>
        }
        title={x.code}
        badges={
          <>
            <MappedBadge map={PO_STATUS} value={x.status} />
            <PoTypeTag type={x.type} />
          </>
        }
        actions={
          <>
            {x.status !== 'CANCELLED' && (
              <Button
                tone="neutral"
                style="stroke"
                onClick={() => setPrint({ kind: 'po', id: x.id })}
                leftIcon={<i className="ri-printer-line text-[14px]" />}
              >
                Print PO
              </Button>
            )}
            {x.status === 'DRAFT' && (
              <Button
                tone="neutral"
                style="stroke"
                data-testid="po-edit"
                onClick={() => setEditing(true)}
                leftIcon={<i className="ri-edit-line text-[14px]" />}
              >
                Ubah
              </Button>
            )}
            {actions.canCancel(x) && (
              <Button
                tone="destructive"
                style="stroke"
                onClick={() => actions.requestCancel(x)}
              >
                Cancel PO
              </Button>
            )}
            {actions.canConfirm(x) && (
              <Button
                loading={actions.busyID === x.id}
                onClick={() => actions.requestConfirm(x)}
                leftIcon={<i className="ri-check-line text-[14px]" />}
              >
                Confirm
              </Button>
            )}
            {x.status === 'CONFIRMED' && (
              <Button
                tone="neutral"
                style="stroke"
                onClick={() =>
                  navigate(`${WAREHOUSE_PATHS.inboundRequest}/${x.id}`)
                }
                leftIcon={<i className="ri-inbox-archive-line text-[14px]" />}
              >
                Lihat inbound request
              </Button>
            )}
            {open && (
              <Button
                onClick={() =>
                  navigate(`${WAREHOUSE_PATHS.goodsReceipt}/new?po=${x.id}`)
                }
                leftIcon={<i className="ri-add-line text-[14px]" />}
              >
                Buat goods receipt
              </Button>
            )}
          </>
        }
      />
      {x.status === 'CANCELLED' && (
        <Alert
          status="warning"
          appearance="stroke"
          size="lg"
          title="PO dibatalkan"
        >
          {x.cancellationReason || '—'} · {fmtDateTime(x.cancelledAt)}
          {x.cancelledBy ? ` · ${x.cancelledBy}` : ''}
        </Alert>
      )}
      <TransferCancelHint po={x} />
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
        <div className="flex flex-col gap-5 xl:col-span-2">
          <Section title="Detail purchase">
            <TableData size="sm" data={lines} columns={LINE_COLUMNS} />
          </Section>
          <Section title="Goods receipt">
            <GoodsReceiptTable rows={goodsReceipts} />
          </Section>
        </div>
        <div className="flex flex-col gap-5">
          <Section title="Info">
            <KV
              label="Client"
              value={x.client?.name ?? `Client #${x.clientID}`}
            />
            <PoSourceKVs po={x} />
            <KV label="Hub tujuan" value={x.hub?.name ?? `Hub #${x.hubID}`} />
            <KV label="Est. arrival" value={fmtDateTime(x.estArrivalAt)} />
            {x.notes && <KV label="Catatan" value={x.notes} />}
          </Section>
          {x.status === 'CONFIRMED' && (
            <Section title="Penerimaan">
              {x.receiptStatus && (
                <KV
                  label="Status penerimaan"
                  value={
                    <MappedBadge map={RECEIPT_STATUS} value={x.receiptStatus} />
                  }
                />
              )}
              <KV
                label="Progress"
                value={
                  <ProgressCell
                    received={x.totalReceived}
                    expected={x.totalQty}
                    format={fmtQty}
                  />
                }
              />
            </Section>
          )}
          <HistorySection entries={purchaseOrderHistory(x, goodsReceipts)} />
        </div>
      </div>
      {x.status === 'DRAFT' && (
        <PurchaseOrderFormModal
          open={editing}
          editing={detail.data}
          initialHubID={x.hubID}
          onClose={() => setEditing(false)}
          onUpdated={() => {
            setEditing(false);
            detail.reload();
          }}
          onStale={() => {
            setEditing(false);
            detail.reload();
          }}
        />
      )}
      {actions.dialogs}
      <WarehousePrintModal target={print} onClose={() => setPrint(null)} />
    </div>
  );
};

export default PurchaseOrderDetailPage;
